import { ref, reactive, computed } from 'vue';
import { REPORT_CONFIG, DEFAULT_FILTERS, PAGINATION_CONFIG } from '../config/reportConfig.js';
import moment from 'moment';

export function useReportState() {
  // Current report state
  const currentReportType = ref(0);
  const isLoading = ref(false);
  const error = ref(null);
  
  // Data state
  const reportData = ref([]);
  const totalCount = ref(0);
  const showCount = ref(false);
  
  // Pagination state
  const currentPage = ref(1);
  const recordsPerPage = ref(PAGINATION_CONFIG.defaultLimit);
  const totalPages = ref(1);
  
  // Filters state
  const filters = reactive({ ...DEFAULT_FILTERS });
  const filterConfig = reactive({});
  
  // Computed properties
  const currentReportConfig = computed(() => {
    return REPORT_CONFIG[currentReportType.value] || REPORT_CONFIG[0];
  });
  
  const supportsPagination = computed(() => {
    return currentReportConfig.value.supportsPagination;
  });
  
  const availableLimits = computed(() => {
    return currentReportConfig.value.availableLimits;
  });
  
  const hasData = computed(() => {
    return reportData.value.length > 0;
  });
  
  const offset = computed(() => {
    return supportsPagination.value ? (currentPage.value - 1) * recordsPerPage.value : 0;
  });
  
  // Methods
  const setReportType = (reportType) => {
    currentReportType.value = reportType;
    clearData();
    updateFilterConfig();
    // Reset filters to appropriate defaults for the new report type
    resetFiltersForReportType();
  };
  
  const resetState = () => {
    isLoading.value = false;
    error.value = null;
    reportData.value = [];
    // Don't reset totalCount here - let it be preserved during loading
    showCount.value = false;
    currentPage.value = 1;
    totalPages.value = 1;
    
    // Reset filters to defaults
    Object.assign(filters, DEFAULT_FILTERS);
  };
  
  const updateFilterConfig = () => {
    const config = currentReportConfig.value;
    Object.assign(filterConfig, {
      hasCallType: config.hasCallType,
      hasAgents: config.hasAgents,
      hasAgentStatus: config.hasAgentStatus,
      hasHungparty: config.hasHungparty,
      hasNumber: config.hasNumber,
      hasDuration: config.hasDuration,
      hasSkill: config.hasSkill,
      hasPeriod: config.hasPeriod,
      hasOmni: config.hasOmni,
      hasReportVariant: config.hasReportVariant || false,
      isNotExport: config.isNotExport,
      useSingleDayDatePicker: config.useSingleDayDatePicker || false
    });
  };
  
  const resetFiltersForReportType = () => {
    const config = currentReportConfig.value;
    
    // Reset to default filters
    Object.assign(filters, { ...DEFAULT_FILTERS });
    
    // Always reset date range to today when changing report type
    const now = moment();
    filters.from = now.startOf('day').format('YYYY-MM-DD HH:mm:ss');
    filters.to = now.endOf('day').format('YYYY-MM-DD HH:mm:ss');
    
    // Clear filters that are not applicable to this report type
    if (!config.hasCallType) filters.callType = null;
    if (!config.hasAgents) filters.agent = null;
    if (!config.hasAgentStatus) filters.agentStatus = null;
    if (!config.hasHungparty) filters.hungparty = null;
    if (!config.hasNumber) filters.number = null;
    if (!config.hasDuration) filters.duration = null;
    if (!config.hasSkill) filters.skill = null;
    if (!config.hasPeriod) filters.period = null;
    if (!config.hasOmni) filters.omniType = null;
    if (!config.hasReportVariant) filters.reportVariant = null;
    
    // Ensure required filters have proper default values for the current report type
    if (config.hasCallType && !filters.callType) filters.callType = 'All';
    if (config.hasAgents && !filters.agent) filters.agent = 'All';
    if (config.hasAgentStatus && !filters.agentStatus) filters.agentStatus = 'All';
    if (config.hasHungparty && !filters.hungparty) filters.hungparty = 'All';
    if (config.hasPeriod && !filters.period) filters.period = 2;
    if (config.hasReportVariant && !filters.reportVariant) filters.reportVariant = 'per-agent';

  };
  
  const setLoading = (loading) => {
    isLoading.value = loading;
  };
  
  const setError = (err) => {
    error.value = err;
    isLoading.value = false;
  };
  
  const clearError = () => {
    error.value = null;
  };
  
  const clearData = () => {
    reportData.value = [];
    totalCount.value = 0;
    showCount.value = false;
    currentPage.value = 1;
    totalPages.value = 1;
  };
  
  const setData = (data, count = 0, isNewRequest = false) => {
    // Only update if data has actually changed to prevent unnecessary re-renders
    const newData = data || [];
    
    // Handle different data types (arrays for most reports, objects for CSAT)
    let dataChanged = false;
    if (Array.isArray(reportData.value) && Array.isArray(newData)) {
      // Both are arrays - compare length and content
      dataChanged = reportData.value.length !== newData.length || 
                   reportData.value.some((item, index) => item !== newData[index]);
    } else {
      // Different types or one is not an array - consider it changed
      dataChanged = JSON.stringify(reportData.value) !== JSON.stringify(newData);
    }
    
    const countChanged = totalCount.value !== count;
    
    
    if (dataChanged) {
      reportData.value = newData;
    }
    
    // Only update count if it's a valid value (greater than 0) or if we're clearing data
    if (countChanged) {
      // Preserve the count during loading states - only update if new count is valid
      if (count > 0 || (count === 0 && (Array.isArray(newData) ? newData.length === 0 : !newData))) {
        totalCount.value = count;
      }
    }
    
    if (isNewRequest && supportsPagination.value) {
      showCount.value = true;
      calculateTotalPages();
    }
  };
  
  const calculateTotalPages = () => {
    if (!supportsPagination.value) {
      totalPages.value = 1;
      return;
    }
    
    const pageCount = Math.floor(totalCount.value / recordsPerPage.value);
    totalPages.value = pageCount > 1 ? pageCount : 1;
    
    if (pageCount >= 1 && totalCount.value > recordsPerPage.value && totalCount.value % recordsPerPage.value > 0) {
      totalPages.value++;
    }
  };
  
  const setPage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
    }
  };
  
  const setRecordsPerPage = (limit) => {
    if (availableLimits.value.includes(limit)) {
      recordsPerPage.value = limit;
      currentPage.value = 1; // Reset to first page when changing records per page
      calculateTotalPages();
    } else {
      console.warn('StateManager: Invalid limit provided:', limit);
    }
  };
  
  const updateFilters = (newFilters) => {
    Object.assign(filters, newFilters);
    currentPage.value = 1; // Reset to first page when filters change
  };
  
  const getRequestPayload = (includeCount = false) => {
    
    const payload = {
      ...filters,
      offset: offset.value,
      limit: recordsPerPage.value,
      iscount: includeCount ? 1 : 0
    };
    
    // Convert duration to seconds if present
    if (payload.duration && typeof payload.duration === 'string') {
      const duration = payload.duration;
      const hours = duration.substr(0, 2);
      const minutes = duration.substr(duration.indexOf(":") + 1, 2);
      const seconds = duration.substr(duration.length - 2, 2);
      payload.duration = +hours * 3600 + +minutes * 60 + +seconds;
    }
    
    // Handle special filter values - convert 'All' to '0' for API compatibility
    if (payload.callType === 'All') payload.callType = '0';
    if (payload.agent === 'All') payload.agent = '0';
    if (payload.hungparty === 'All') payload.hungparty = '0';
    if (payload.agentStatus === 'All') payload.agentStatus = '0';
    if (payload.number === '') payload.number = '0';
    if (payload.duration === '') payload.duration = '0';
    
    // Skill is already a string (skill name) from the UI component
    
    return payload;
  };
  
  // Initialize filter config
  updateFilterConfig();
  
  return {
    // State
    currentReportType,
    isLoading,
    error,
    reportData,
    totalCount,
    showCount,
    currentPage,
    recordsPerPage,
    totalPages,
    filters,
    filterConfig,
    
    // Computed
    currentReportConfig,
    supportsPagination,
    availableLimits,
    hasData,
    offset,
    
    // Methods
    setReportType,
    resetState,
    setLoading,
    setError,
    clearError,
    clearData,
    setData,
    setPage,
    setRecordsPerPage,
    updateFilters,
    resetFiltersForReportType,
    getRequestPayload
  };
} 